import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Shield, LayoutGrid, Users, CreditCard, CalendarClock, Package, QrCode, Settings,
  LogOut, Menu, CheckCircle2, XCircle, Ban, Play, Clock, IndianRupee, FileText, Star,
} from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutGrid },
  { id: "users", label: "Users", icon: Users },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "subscriptions", label: "Subscriptions", icon: CalendarClock },
  { id: "plans", label: "Plans", icon: Package },
  { id: "qrcodes", label: "QR Codes", icon: QrCode },
  { id: "policies", label: "Policies", icon: FileText },
  { id: "pages", label: "Use-case Pages", icon: FileText },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "settings", label: "Settings", icon: Settings },
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");
const stateBadge = (s) => {
  const map = { ACTIVE: "bg-accent text-accent-foreground", EXPIRING_SOON: "bg-yellow-500 text-white", EXPIRED: "bg-destructive text-destructive-foreground", DISABLED: "bg-zinc-500 text-white", PAYMENT_PENDING: "bg-secondary text-secondary-foreground" };
  return <Badge className={map[s] || "bg-secondary"}>{s.replace("_", " ")}</Badge>;
};

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-border bg-card p-5">
    <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="mt-2 font-display text-3xl font-extrabold tracking-tighter">{value}</p>
  </div>
);

export default function AdminApp() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Button variant="ghost" size="icon" className="lg:hidden rounded-full" onClick={() => setNavOpen(!navOpen)}><Menu className="h-5 w-5" /></Button>
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-foreground text-background"><Shield className="h-4 w-4" /></div>
            <span className="font-display text-lg font-extrabold tracking-tighter">DynaQR Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-sm font-medium">{user.email}</span>
            <Button variant="outline" size="icon" className="rounded-full" onClick={logout} data-testid="admin-logout"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className={`lg:col-span-3 xl:col-span-2 ${navOpen ? "block" : "hidden"} lg:block`}>
          <nav className="lg:sticky lg:top-20 space-y-1">
            {NAV.map((n) => (
              <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => { setTab(n.id); setNavOpen(false); }}
                className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${tab === n.id ? "bg-foreground text-background" : "hover:bg-secondary"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="lg:col-span-9 xl:col-span-10">
          {tab === "dashboard" && <AdminDashboard />}
          {tab === "users" && <AdminUsers />}
          {tab === "payments" && <AdminPayments />}
          {tab === "subscriptions" && <AdminSubscriptions />}
          {tab === "plans" && <AdminPlans />}
          {tab === "qrcodes" && <AdminQrcodes />}
          {tab === "policies" && <AdminPolicies />}
          {tab === "pages" && <AdminPages />}
          {tab === "testimonials" && <AdminTestimonials />}
          {tab === "settings" && <AdminSettings />}
        </main>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.adminOverview().then(setD); }, []);
  if (!d) return null;
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Dashboard</h1>
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Users</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Total" value={d.users.total} testid="admin-total-users" />
          <Stat label="Active" value={d.users.active} />
          <Stat label="Expired" value={d.users.expired} />
          <Stat label="Disabled" value={d.users.disabled} />
          <Stat label="Pending" value={d.users.payment_pending} />
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Revenue</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Stat label="Approved revenue" value={`₹${d.revenue.approved}`} testid="admin-revenue" />
          <Stat label="Total payments" value={d.revenue.total_payments} />
          <Stat label="Pending payments" value={d.revenue.pending} />
        </div>
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Subscriptions & QR</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Expiring 30d" value={d.subscriptions.expiring_30} />
          <Stat label="Expiring 7d" value={d.subscriptions.expiring_7} />
          <Stat label="Total QR" value={d.qr.total} />
          <Stat label="Total scans" value={d.qr.total_scans} />
        </div>
      </div>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [referralFilter, setReferralFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (referralFilter !== "all") params.referral = referralFilter;
    api.adminUsers(params).then(setUsers);
  }, [statusFilter, referralFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-users">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Users</h1>
      <div className="flex flex-wrap gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="user-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "ACTIVE", "EXPIRING_SOON", "EXPIRED", "DISABLED", "PAYMENT_PENDING"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={referralFilter} onValueChange={setReferralFilter}>
          <SelectTrigger className="w-40" data-testid="user-referral-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "Referral", "Event", "Social Media", "WhatsApp", "LinkedIn", "Google", "Friend", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
            <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Source</th><th className="p-3">Status</th><th className="p-3">Expiry</th><th className="p-3">Scans</th><th className="p-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border" data-testid={`user-row-${u.id}`}>
                <td className="p-3"><div className="font-medium">{u.full_name}</div><div className="text-xs text-muted-foreground">{u.business_name}</div></td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.referral_source || "—"}</td>
                <td className="p-3">{stateBadge(u.state)}</td>
                <td className="p-3">{fmt(u.subscription?.expiry_date)}</td>
                <td className="p-3">{u.total_scans}</td>
                <td className="p-3"><Button size="sm" variant="outline" className="rounded-full" onClick={() => setSelected(u.id)} data-testid={`user-view-${u.id}`}>View</Button></td>
              </tr>
            ))}
            {!users.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No users found.</td></tr>}
          </tbody>
        </table>
      </div>
      {selected && <UserDetailDialog userId={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function UserDetailDialog({ userId, onClose, onChanged }) {
  const [u, setU] = useState(null);
  const [extendDays, setExtendDays] = useState("365");
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.adminUser(userId).then(setU), [userId]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (fn, msg) => { await fn(); toast.success(msg); await load(); onChanged(); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-auto" data-testid="user-detail-dialog">
        {u && (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">{u.full_name}</DialogTitle></DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap gap-2 items-center">{stateBadge(u.state)}<span className="text-muted-foreground">{u.email} · {u.mobile}</span></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Business</p>{u.business_name || "—"}</div>
                <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Source / Chapter</p>{u.referral_source || "—"} / {u.chapter || "—"}</div>
                <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Expiry</p>{fmt(u.subscription?.expiry_date)}</div>
                <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Scans / Events</p>{u.total_scans} / {u.total_events}</div>
              </div>

              <div className="flex flex-wrap gap-2">
                {u.account_status === "disabled"
                  ? <Button size="sm" className="rounded-full" onClick={() => doAction(() => api.adminActivate(userId), "User activated")} data-testid="user-activate-btn"><Play className="h-4 w-4 mr-1" />Activate</Button>
                  : <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => doAction(() => api.adminDisable(userId), "User disabled")} data-testid="user-disable-btn"><Ban className="h-4 w-4 mr-1" />Disable</Button>}
              </div>

              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="font-medium">Extend subscription</p>
                <div className="flex flex-wrap gap-2 items-end">
                  <Select value={extendDays} onValueChange={setExtendDays}>
                    <SelectTrigger className="w-36" data-testid="extend-days-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">+30 days</SelectItem>
                      <SelectItem value="90">+90 days</SelectItem>
                      <SelectItem value="365">+365 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input className="w-48" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Button size="sm" className="rounded-full" data-testid="extend-btn"
                    onClick={() => doAction(() => api.adminExtend(userId, { days: Number(extendDays), reason }), "Subscription extended")}>Extend</Button>
                </div>
              </div>

              {u.payments?.length > 0 && (
                <div className="rounded-lg border border-border p-3">
                  <p className="font-medium mb-2">Payments</p>
                  {u.payments.map((p) => <div key={p.id} className="flex justify-between text-xs py-1"><span>{p.payment_date} · {p.utr}</span><Badge className={p.status === "approved" ? "bg-accent text-accent-foreground" : "bg-secondary"}>{p.status}</Badge></div>)}
                </div>
              )}
              {u.activities?.length > 0 && (
                <div className="rounded-lg border border-border p-3">
                  <p className="font-medium mb-2">Activity</p>
                  {u.activities.map((a) => <div key={a.id} className="text-xs text-muted-foreground py-1">{fmt(a.created_at)} · {a.detail} <span className="opacity-60">({a.by})</span></div>)}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(() => api.adminPayments(filter === "all" ? null : filter).then(setPayments), [filter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { await api.adminApprove(id); toast.success("Payment approved & subscription activated"); load(); };
  const reject = async (id) => { const r = prompt("Rejection reason:"); if (r) { await api.adminReject(id, r); toast.success("Payment rejected"); load(); } };

  return (
    <div className="space-y-4" data-testid="admin-payments">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold tracking-tighter">Payments</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["pending", "approved", "rejected", "all"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-3">
        {payments.map((p) => (
          <div key={p.id} className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`payment-row-${p.id}`}>
            <div>
              <p className="font-medium">{p.user?.full_name} <span className="text-muted-foreground font-normal">· {p.user?.email}</span></p>
              <p className="text-sm text-muted-foreground">SOLO · ₹{p.amount} · UTR {p.utr} · {p.payment_date}</p>
              {p.admin_notes && <p className="text-xs text-muted-foreground mt-1">Note: {p.admin_notes}</p>}
              {p.screenshot && <ScreenshotLink path={p.screenshot} />}
            </div>
            {p.status === "pending" ? (
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full" onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>
                <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
              </div>
            ) : <Badge className={p.status === "approved" ? "bg-accent text-accent-foreground" : "bg-destructive text-destructive-foreground"}>{p.status}</Badge>}
          </div>
        ))}
        {!payments.length && <p className="text-muted-foreground text-center py-8">No payments.</p>}
      </div>
    </div>
  );
}

function ScreenshotLink({ path }) {
  const [url, setUrl] = useState(null);
  const view = async () => {
    try { const u = await api.fileBlobUrl(path); setUrl(u); }
    catch { toast.error("Could not load screenshot"); }
  };
  return (
    <div className="mt-1">
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-xs text-accent underline" data-testid="screenshot-open">Open screenshot ↗</a>
      ) : (
        <button onClick={view} className="text-xs text-accent underline" data-testid="screenshot-view">View screenshot</button>
      )}
    </div>
  );
}

function AdminSubscriptions() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.adminSubscriptions().then(setSubs); }, []);
  return (
    <div className="space-y-4" data-testid="admin-subscriptions">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Subscriptions</h1>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
            <th className="p-3">User</th><th className="p-3">Start</th><th className="p-3">Expiry</th><th className="p-3">Days left</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.user_id} className="border-b border-border">
                <td className="p-3">{s.user?.full_name}</td><td className="p-3">{fmt(s.start_date)}</td><td className="p-3">{fmt(s.expiry_date)}</td>
                <td className="p-3">{s.days_remaining}</td><td className="p-3">{stateBadge(s.state)}</td>
              </tr>
            ))}
            {!subs.length && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No subscriptions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminPlans() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.adminPlans().then(setPlans); }, []);
  return (
    <div className="space-y-4" data-testid="admin-plans">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Plans</h1>
      {plans.map((p) => (
        <div key={p.id} className="rounded-2xl border border-border bg-card p-6 max-w-md">
          <p className="font-display text-xl font-bold">{p.name} · ₹{p.price}/{p.duration_days}d</p>
          <p className="text-sm text-muted-foreground mt-1">QR limit: {p.qr_limit} · Events: {p.event_limit === -1 ? "Unlimited" : p.event_limit}</p>
          <ul className="mt-3 space-y-1">{p.features.map((f) => <li key={f} className="text-sm flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent" />{f}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}

function AdminQrcodes() {
  const [qrs, setQrs] = useState([]);
  useEffect(() => { api.adminQrcodes().then(setQrs); }, []);
  return (
    <div className="space-y-4" data-testid="admin-qrcodes">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">QR Codes</h1>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
            <th className="p-3">Owner</th><th className="p-3">Slug</th><th className="p-3">Status</th><th className="p-3">Scans</th></tr></thead>
          <tbody>
            {qrs.map((q) => (
              <tr key={q.id} className="border-b border-border">
                <td className="p-3">{q.user?.full_name}</td><td className="p-3 font-mono text-xs">{q.slug}</td>
                <td className="p-3"><Badge className={q.active ? "bg-accent text-accent-foreground" : "bg-secondary"}>{q.active ? "active" : "inactive"}</Badge></td>
                <td className="p-3">{q.total_scans}</td>
              </tr>
            ))}
            {!qrs.length && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No QR codes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminPolicies() {
  const [policies, setPolicies] = useState([]);
  const [slug, setSlug] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const load = () => api.adminPolicies().then((ps) => { setPolicies(ps); if (!slug && ps.length) select(ps[0]); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const select = (p) => { setSlug(p.id); setTitle(p.title); setContent(p.content || ""); };
  const save = async () => { await api.updatePolicy(slug, { title, content }); toast.success("Policy saved"); load(); };
  return (
    <div className="space-y-4" data-testid="admin-policies">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Policies</h1>
      <div className="flex flex-wrap gap-2">
        {policies.map((p) => (
          <Button key={p.id} size="sm" variant={slug === p.id ? "default" : "outline"} className="rounded-full"
            onClick={() => select(p)} data-testid={`policy-tab-${p.id}`}>{p.title}</Button>
        ))}
      </div>
      {slug && (
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="policy-title-input" /></div>
          <div className="space-y-2">
            <Label>Content (HTML supported — e.g. &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;)</Label>
            <Textarea rows={16} value={content} onChange={(e) => setContent(e.target.value)} data-testid="policy-content-input" className="font-mono text-xs" />
          </div>
          <div className="flex items-center gap-3">
            <Button className="rounded-full" onClick={save} data-testid="policy-save-btn">Save policy</Button>
            <a href={`/policy/${slug}`} target="_blank" rel="noreferrer" className="text-sm text-accent hover:underline">Preview public page ↗</a>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // {slug?, title, content, published, isNew}
  const load = () => api.adminPages().then(setPages);
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ slug: "", title: "", content: "<p>Write your page content here. HTML is supported.</p>", published: true, isNew: true });
  const save = async () => {
    try {
      if (editing.isNew) await api.createPage({ slug: editing.slug, title: editing.title, content: editing.content, published: editing.published });
      else await api.updatePage(editing.slug, { title: editing.title, content: editing.content, published: editing.published });
      toast.success("Page saved"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };
  const del = async (slug) => { if (window.confirm("Delete this page?")) { await api.deletePage(slug); toast.success("Deleted"); load(); } };

  return (
    <div className="space-y-4" data-testid="admin-pages">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold tracking-tighter">Use-case Pages</h1>
        <Button className="rounded-full" onClick={startNew} data-testid="page-new-btn">New page</Button>
      </div>
      {!editing ? (
        <div className="space-y-2">
          {pages.map((p) => (
            <div key={p.slug} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3" data-testid={`page-row-${p.slug}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{p.title} {!p.published && <Badge className="bg-secondary ml-1">draft</Badge>}</p>
                <a href={`/page/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">/page/{p.slug} ↗</a>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing({ ...p, isNew: false })} data-testid={`page-edit-${p.slug}`}>Edit</Button>
                <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => del(p.slug)} data-testid={`page-delete-${p.slug}`}>Delete</Button>
              </div>
            </div>
          ))}
          {!pages.length && <p className="text-muted-foreground text-center py-8">No pages yet. Create SEO landing pages like "Dynamic QR for events".</p>}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="page-title-input" /></div>
            <div className="space-y-2"><Label>Slug (URL) {editing.isNew ? "" : "(fixed)"}</Label><Input value={editing.slug} disabled={!editing.isNew} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="dynamic-qr-for-events" data-testid="page-slug-input" /></div>
          </div>
          <div className="space-y-2">
            <Label>Content (HTML)</Label>
            <Textarea rows={14} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="font-mono text-xs" data-testid="page-content-input" />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-[#00C853]" data-testid="page-published-toggle" /> Published</label>
          <div className="flex gap-2">
            <Button className="rounded-full" onClick={save} data-testid="page-save-btn">Save page</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminTestimonials() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.adminGetTestimonials().then(setItems); }, []);
  const upd = (i, k, v) => setItems(items.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const add = () => setItems([...items, { name: "", role: "", quote: "" }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const save = async () => {
    const clean = items.filter((t) => t.name.trim() && t.quote.trim());
    await api.updateTestimonials(clean); setItems(clean); toast.success("Testimonials saved");
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-testimonials">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold tracking-tighter">Testimonials</h1>
        <Button size="sm" className="rounded-full" onClick={add} data-testid="testimonial-add-btn">Add</Button>
      </div>
      {items.map((t, i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid={`testimonial-${i}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Name</Label><Input value={t.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`testimonial-name-${i}`} /></div>
            <div className="space-y-1"><Label>Role</Label><Input value={t.role} onChange={(e) => upd(i, "role", e.target.value)} data-testid={`testimonial-role-${i}`} /></div>
          </div>
          <div className="space-y-1"><Label>Quote</Label><Textarea rows={2} value={t.quote} onChange={(e) => upd(i, "quote", e.target.value)} data-testid={`testimonial-quote-${i}`} /></div>
          <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => remove(i)} data-testid={`testimonial-remove-${i}`}>Remove</Button>
        </div>
      ))}
      {!items.length && <p className="text-muted-foreground">No testimonials. Click Add to create one.</p>}
      <Button className="rounded-full" onClick={save} data-testid="testimonials-save-btn">Save testimonials</Button>
      <p className="text-xs text-muted-foreground">These appear on the public landing page. Empty entries are skipped.</p>
    </div>
  );
}

function AdminSettings() {
  const [s, setS] = useState({ upi_id: "", payee_name: "", sender_email: "", support_whatsapp: "" });
  useEffect(() => { api.adminSettings().then((d) => setS({ upi_id: d.upi_id || "", payee_name: d.payee_name || "", sender_email: d.sender_email || "", support_whatsapp: d.support_whatsapp || "" })); }, []);
  const save = async () => { await api.updateAdminSettings(s); toast.success("Settings saved"); };
  return (
    <div className="space-y-4 max-w-md" data-testid="admin-settings">
      <h1 className="font-display text-2xl font-extrabold tracking-tighter">Settings</h1>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <p className="font-medium">Email sender</p>
        <div className="space-y-2">
          <Label>From email address</Label>
          <Input data-testid="sender-email-input" value={s.sender_email} onChange={(e) => setS({ ...s, sender_email: e.target.value })} placeholder="DynaQR <noreply@dynaqr.in>" />
          <p className="text-xs text-muted-foreground">Emails are sent from this address. It MUST be on a domain verified in Resend (e.g. dynaqr.in). Format: <code>Name &lt;email@dynaqr.in&gt;</code>.</p>
        </div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <p className="font-medium">Payment (UPI)</p>
        <div className="space-y-2"><Label>UPI ID</Label><Input data-testid="upi-id-input" value={s.upi_id} onChange={(e) => setS({ ...s, upi_id: e.target.value })} placeholder="yourname@okhdfcbank" /></div>
        <div className="space-y-2"><Label>Payee name</Label><Input data-testid="payee-name-input" value={s.payee_name} onChange={(e) => setS({ ...s, payee_name: e.target.value })} placeholder="DynaQR" /></div>
      </div>
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <p className="font-medium">WhatsApp support</p>
        <div className="space-y-2">
          <Label>Support WhatsApp number (with country code)</Label>
          <Input data-testid="support-whatsapp-input" value={s.support_whatsapp} onChange={(e) => setS({ ...s, support_whatsapp: e.target.value })} placeholder="e.g. 919876543210" />
          <p className="text-xs text-muted-foreground">Shown as a floating "Need help?" button across the user side. Leave blank to hide it.</p>
        </div>
      </div>
      <Button className="rounded-full" onClick={save} data-testid="save-settings-btn">Save</Button>
    </div>
  );
}
